import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Camera, CheckCircle2, Link as LinkIcon, Mail, MapPin, Phone, Plane, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1599696848652-f0ff23bc911f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODU3NTY5NjV8MA&ixlib=rb-4.1.0&q=85";
const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_magic-link-journey/artifacts/zmxbnelr_Reelty_Logo_R-Only.png";

// Matches backend APPLICANT_REGIONS. Keeping the list in-file (not shared
// via lib/) because it changes rarely and mismatches surface immediately.
const REGIONS = [
  "North Carolina",
  "South Carolina",
  "Georgia",
  "Tennessee",
  "Virginia",
];

const initialForm = {
  applied_role: "photographer", // photographer | pilot
  name: "",
  email: "",
  phone: "",
  zip_code: "",
  region: "North Carolina",
  resume_url: "",
  gear: "",
  desired_location: "",
  notes: "",
  website: "", // honeypot — must stay empty
};

// Role-specific copy so a Pilot doesn't see camera-oriented prompts and a
// Photographer doesn't see drone-oriented prompts. Kept in this file
// because they don't need to be reused anywhere else.
const ROLE_COPY = {
  photographer: {
    heroTitle: (<>Shoot with Reelty</>),
    heroSub:
      "We're building the residential real-estate photography roster across the Carolinas, Georgia, Tennessee, and Virginia. Tell us who you are, where you shoot, and what's in your kit.",
    gearLabel: "Your camera gear",
    gearHint:
      "Camera bodies, lenses, tripods, lighting — anything you'd bring to a residential shoot.",
    gearPlaceholder:
      "Sony A7R V\n16-35 GM, 24-70 GM II, 70-200 GM\nGodox AD200, Manfrotto 190 tripod",
    portfolioLabel: "Portfolio or resume URL",
    submitLabel: "Submit photographer application",
  },
  pilot: {
    heroTitle: (<>Fly with Reelty</>),
    heroSub:
      "We're growing our FAA Part 107 pilot roster to capture aerial and rooftop footage on Reelty residential shoots. Tell us who you are, where you fly, and which drone(s) you own.",
    gearLabel: "Your drone gear + FAA Part 107 status",
    gearHint:
      "List your drone bodies + any ND filters / spare batteries. Include your Part 107 cert number and expiry — Reelty requires an active Part 107 for every paid flight.",
    gearPlaceholder:
      "DJI Mavic 3 Pro, Mavic 3 Cine (spare)\n2x ND16/64 filters, 6 batteries\nFAA Part 107 · Cert #4XXXXXX · Expires 2027-05",
    portfolioLabel: "Reel or aerial portfolio URL",
    submitLabel: "Submit pilot application",
  },
};

export default function ApplyPage() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const roleCopy = ROLE_COPY[form.applied_role] || ROLE_COPY.photographer;

  const submit = async (e) => {
    e.preventDefault();
    // Client-side guardrails so we don't round-trip on obviously-empty forms.
    const required = ["name", "email", "phone", "zip_code", "region", "resume_url", "gear"];
    for (const key of required) {
      if (!String(form[key] || "").trim()) {
        toast.error("Please fill in every required field.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Trim leading/trailing whitespace on every string field before send.
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === "string") payload[k] = payload[k].trim();
      });
      await api.post("/public/apply", payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" · ")
          : "Could not submit your application. Please double-check your details and try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EBD9]" data-testid="apply-page">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] min-h-screen">
        {/* ------------ Hero panel ------------ */}
        <div className="relative flex items-center justify-center p-8 md:p-16 bg-[#1A0F0A] text-[#F5EBD9] overflow-hidden lg:sticky lg:top-0 lg:h-screen">
          <div
            aria-hidden
            className="absolute inset-0 opacity-70"
            style={{
              background:
                "radial-gradient(1200px 700px at 20% 100%, rgba(107,15,26,0.35), transparent 60%), radial-gradient(900px 600px at 100% 0%, rgba(176,141,87,0.12), transparent 60%)",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.12] mix-blend-luminosity"
            style={{
              backgroundImage: `url(${HERO_IMAGE})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="relative z-10 w-full max-w-md">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex flex-col items-start gap-2 mb-10">
                <img
                  src={LOGO_URL}
                  alt="Reelty"
                  className="w-[60px] h-[60px] object-contain -ml-[19px]"
                />
                <div>
                  <div className="font-mono-tag text-[10px] tracking-[0.35em] text-[#F5EBD9]">REELTY</div>
                  <div className="font-mono-tag text-[10px] tracking-[0.25em] text-[#78584B]">
                    {form.applied_role === "pilot" ? "PILOT APPLICATION" : "PHOTOGRAPHER APPLICATION"}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.h1
              key={form.applied_role}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-3xl md:text-[44px] font-extrabold tracking-tight leading-[1.05]"
            >
              {roleCopy.heroTitle}
            </motion.h1>

            <motion.p
              key={`sub-${form.applied_role}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.06 }}
              className="mt-6 text-[15px] text-[#D6C7A6] leading-relaxed max-w-sm"
            >
              {roleCopy.heroSub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.24 }}
              className="mt-10 space-y-3.5"
            >
              {[
                "Set your own service area and radius.",
                "Pay range: $50 – $75/hour.",
                "No editing required — Reelty handles post-production.",
                "Automated dispatch — pick shoots that fit your calendar.",
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-3 text-[#F5EBD9]">
                  <CheckCircle2 className="w-4 h-4 mt-1 text-[#B08D57] flex-shrink-0" strokeWidth={2} />
                  <span className="text-sm leading-relaxed">{line}</span>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-14 pt-6 border-t border-[#F5EBD9]/10 font-mono-tag text-[10px] tracking-[0.22em] text-[#78584B]"
            >
              QUESTIONS? &nbsp;·&nbsp; SUPPORT@GOREELTY.COM
            </motion.div>
          </div>
        </div>

        {/* ------------ Form / Success panel ------------ */}
        <div className="flex items-start justify-center p-8 md:p-14 lg:p-16">
          <div className="w-full max-w-xl">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="apply-success"
                className="pt-12"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#3F7A2E]/15 border border-[#3F7A2E]/40 mb-6">
                  <CheckCircle2 className="w-7 h-7 text-[#3F7A2E]" strokeWidth={2} />
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[#1A0F0A] tracking-tight leading-tight">
                  Application received.
                </h2>
                <p className="mt-4 text-[15px] text-[#1A0F0A] leading-relaxed">
                  Thanks for applying, <span className="font-semibold">{form.name.split(" ")[0] || "there"}</span>.
                  A Reelty producer will review your gear list and portfolio
                  and reply within a few business days.
                </p>
                <p className="mt-3 text-[13px] text-[#78584B] leading-relaxed">
                  A confirmation is on its way to <span className="text-[#1A0F0A] font-medium">{form.email}</span>.
                  If you don't see it in ten minutes, check your spam folder.
                </p>
                <div className="mt-10 pt-6 border-t border-[#1A0F0A]/10 font-mono-tag text-[10px] tracking-[0.22em] text-[#78584B]">
                  YOU CAN CLOSE THIS TAB
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="apply-form">
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#1A0F0A] tracking-tight">
                    Tell us about you.
                  </h2>
                  <p className="mt-2 text-[13px] text-[#78584B]">
                    Fields marked <span className="text-[#6B0F1A]">*</span> are required.
                  </p>
                </div>

                {/* Role picker — segmented control at the top of the form.
                    Only Photographer & Pilot are exposed publicly; Employee
                    is admin-invite-only. Toggling this switches gear-field
                    copy + hero copy live. */}
                <div>
                  <label className="font-mono-tag text-[10px] tracking-[0.18em] text-[#78584B] mb-2 block">
                    I'M APPLYING AS <span className="text-[#6B0F1A]">*</span>
                  </label>
                  <div
                    role="radiogroup"
                    aria-label="Applying as"
                    className="grid grid-cols-2 gap-2"
                    data-testid="apply-role-picker"
                  >
                    {[
                      { key: "photographer", label: "Photographer", icon: Camera, blurb: "Real-estate stills + interiors" },
                      { key: "pilot",        label: "Drone Pilot",  icon: Plane,  blurb: "FAA Part 107 aerials" },
                    ].map((r) => {
                      const selected = form.applied_role === r.key;
                      const Icon = r.icon;
                      return (
                        <button
                          key={r.key}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => setForm((f) => ({ ...f, applied_role: r.key }))}
                          data-testid={`apply-role-${r.key}`}
                          className={`text-left p-3.5 rounded-sm border transition-all ${
                            selected
                              ? "border-[#6B0F1A] bg-[#6B0F1A]/6 shadow-[inset_0_0_0_1px_rgba(107,15,26,0.3)]"
                              : "border-[#1A0F0A]/15 bg-white hover:border-[#1A0F0A]/35"
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon
                              className={`w-4 h-4 ${selected ? "text-[#6B0F1A]" : "text-[#78584B]"}`}
                              strokeWidth={2}
                            />
                            <span
                              className={`text-sm font-bold tracking-tight ${
                                selected ? "text-[#6B0F1A]" : "text-[#1A0F0A]"
                              }`}
                            >
                              {r.label}
                            </span>
                          </div>
                          <div className="text-[11px] text-[#78584B] leading-snug">
                            {r.blurb}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Honeypot — visually hidden but present in the DOM so bots
                    that auto-fill every input will populate it. Real humans
                    never see this field. `tabIndex=-1` + `autoComplete="off"`
                    keeps assistive tech from surfacing it either. */}
                <div aria-hidden className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden">
                  <label>
                    Do not fill this field
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </label>
                </div>

                <Field icon={User} label="Full name" required htmlFor="apply-name">
                  <Input
                    id="apply-name"
                    data-testid="apply-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Alex Photographer"
                    required
                    autoComplete="name"
                  />
                </Field>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field icon={Mail} label="Email" required htmlFor="apply-email">
                    <Input
                      id="apply-email"
                      data-testid="apply-email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@studio.com"
                      required
                      autoComplete="email"
                    />
                  </Field>
                  <Field icon={Phone} label="Phone" required htmlFor="apply-phone">
                    <Input
                      id="apply-phone"
                      data-testid="apply-phone"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="555 555 5555"
                      required
                      autoComplete="tel"
                    />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-[1fr_1.5fr] gap-5">
                  <Field icon={MapPin} label="Base ZIP" required htmlFor="apply-zip">
                    <Input
                      id="apply-zip"
                      data-testid="apply-zip"
                      value={form.zip_code}
                      onChange={set("zip_code")}
                      placeholder="28202"
                      required
                      inputMode="numeric"
                      pattern="[0-9]{5}"
                      maxLength={5}
                      autoComplete="postal-code"
                    />
                  </Field>
                  <Field label="Region" required htmlFor="apply-region">
                    <select
                      id="apply-region"
                      data-testid="apply-region"
                      value={form.region}
                      onChange={set("region")}
                      required
                      className="flex h-10 w-full rounded-sm border border-[#1A0F0A]/15 bg-white px-3 py-2 text-sm text-[#1A0F0A] focus:outline-none focus:ring-2 focus:ring-[#6B0F1A]/40"
                    >
                      {REGIONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="City you'd typically shoot" htmlFor="apply-city">
                  <Input
                    id="apply-city"
                    data-testid="apply-city"
                    value={form.desired_location}
                    onChange={set("desired_location")}
                    placeholder="Charlotte, NC"
                    autoComplete="address-level2"
                  />
                </Field>

                <Field icon={LinkIcon} label={roleCopy.portfolioLabel} required htmlFor="apply-portfolio">
                  <Input
                    id="apply-portfolio"
                    data-testid="apply-portfolio"
                    type="url"
                    value={form.resume_url}
                    onChange={set("resume_url")}
                    placeholder="https://your-portfolio.com"
                    required
                    autoComplete="url"
                  />
                </Field>

                <Field
                  icon={form.applied_role === "pilot" ? Plane : Camera}
                  label={roleCopy.gearLabel}
                  required
                  htmlFor="apply-gear"
                  hint={roleCopy.gearHint}
                >
                  <Textarea
                    id="apply-gear"
                    data-testid="apply-gear"
                    value={form.gear}
                    onChange={set("gear")}
                    placeholder={roleCopy.gearPlaceholder}
                    required
                    rows={5}
                    className="resize-y"
                  />
                </Field>

                <Field label="Anything else we should know?" htmlFor="apply-notes">
                  <Textarea
                    id="apply-notes"
                    data-testid="apply-notes"
                    value={form.notes}
                    onChange={set("notes")}
                    placeholder="Years shooting real estate, availability, weekend blackout dates…"
                    rows={3}
                    className="resize-y"
                  />
                </Field>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="apply-submit"
                  className="w-full h-12 bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-sm tracking-wide rounded-sm inline-flex items-center justify-center gap-2 btn-primary-glow"
                >
                  {submitting ? "Submitting…" : (<>{roleCopy.submitLabel} <ArrowRight className="w-4 h-4" strokeWidth={2} /></>)}
                </Button>

                <p className="text-[11px] text-[#78584B] leading-relaxed pt-2">
                  By submitting you agree to be contacted by Reelty about
                  photographer opportunities. We never share your details.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, required, htmlFor, hint, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="font-mono-tag text-[10px] tracking-[0.18em] text-[#78584B] mb-1.5 flex items-center gap-1.5"
      >
        {Icon && <Icon className="w-3 h-3 text-[#B08D57]" strokeWidth={2} />}
        {label.toUpperCase()}
        {required && <span className="text-[#6B0F1A]">*</span>}
      </label>
      {children}
      {hint && (
        <p className="mt-1.5 text-[11px] text-[#78584B] leading-relaxed">{hint}</p>
      )}
    </div>
  );
}
